import React from 'react';
import { Paper, alpha, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { formatClientName } from '../utils';

const ReservationCard = ({ reservation, color, isIntervenant, onClick }) => {
  // 1. Récupération du rôle utilisateur depuis originalData
  const roleUser =
    reservation.originalData?.user?.role_user ?? reservation.user?.role_user;

  // Afficher la section compte-rendu UNIQUEMENT si le role_user est égal à 4 (Praticien)
  const isPraticien = Number(roleUser) === 4;

  // 2. Vérification robuste de la présence d'un compte rendu
  const compteRenduObj =
    reservation.comptes_rendus || reservation.originalData?.comptes_rendus;
  const hasCompteRendu = Boolean(
    compteRenduObj && Object.keys(compteRenduObj).length > 0
  );

  // 3. Statut de la réservation
  const statusReservation =
    reservation.statusFr ||
    reservation.status ||
    reservation.originalData?.status_reservation;

  // 4. Vérification Présentiel Locaux SOA & Statut de validation / refus
  const infoOffre =
    reservation.originalData?.offre?.info_offre ||
    reservation.offre?.info_offre ||
    {};

  const isPresentielLocauxSoa =
    infoOffre.format_offre === 'presentiel' &&
    infoOffre.location_type === 'locaux_soa';

  const rawValidated = String(
    reservation.originalData?.is_validated || reservation.is_validated || ''
  ).toLowerCase();

  const isValidated =
    rawValidated === 'valide' ||
    rawValidated === 'valider' ||
    rawValidated === 'true' ||
    rawValidated === '1';

  // Détection explicite du refus par le super admin
  const isRefusedByAdmin =
    rawValidated === 'refuse' ||
    rawValidated === 'refusé' ||
    statusReservation === 'cancelled' ||
    statusReservation === 'refuse';

  return (
    <Paper
      onClick={onClick}
      sx={{
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${color}`,
        px: 1,
        py: 0.6,
        borderRadius: '6px',
        fontSize: '0.65rem',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {/* Horaires */}
      <div style={{ fontWeight: 800 }}>
        {reservation.heureDebut} — {reservation.heureFin}
      </div>

      {/* Nom du client */}
      <div>
        {reservation.client || reservation.originalData?.client
          ? formatClientName(
              reservation.client || reservation.originalData?.client,
              isIntervenant
            )
          : 'Client inconnu'}
      </div>

      {/* Badge spécifique Présentiel Locaux SOA (Validé, Refusé ou Non validé) */}
      {isPresentielLocauxSoa && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            px: 0.6,
            py: '2px',
            borderRadius: '4px',
            bgcolor: isRefusedByAdmin
              ? alpha('#EF4444', 0.15)
              : isValidated
              ? alpha('#10B981', 0.15)
              : alpha('#F59E0B', 0.2),
            color: isRefusedByAdmin ? '#DC2626' : isValidated ? '#047857' : '#B45309',
            fontSize: '0.55rem',
            fontWeight: 700,
            border: isRefusedByAdmin
              ? '1px solid #EF4444'
              : isValidated
              ? '1px solid #10B981'
              : '1px dashed #F59E0B',
          }}
        >
          {isRefusedByAdmin ? (
            <>
              <CancelOutlinedIcon sx={{ fontSize: '0.75rem' }} />
              <span>Refusé (Locaux SOA)</span>
            </>
          ) : isValidated ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: '0.75rem' }} />
              <span>Validé (Locaux SOA)</span>
            </>
          ) : (
            <>
              <WarningAmberIcon sx={{ fontSize: '0.75rem' }} />
              <span>Non validé (Locaux SOA)</span>
            </>
          )}
        </Box>
      )}

      {/* Statut de la réservation */}
      {statusReservation && (
        <Box
          sx={{
            alignSelf: 'flex-start',
            px: 0.6,
            py: '1px',
            borderRadius: '4px',
            bgcolor: alpha(color, 0.25),
            color: color,
            fontSize: '0.55rem',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {statusReservation}
        </Box>
      )}

      {/* Ligne Compte Rendu : Affichée UNIQUEMENT pour role_user === 4 */}
      {isPraticien && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            mt: '2px',
            pt: '2px',
            borderTop: '1px dashed rgba(0,0,0,0.1)',
          }}
        >
          {hasCompteRendu ? (
            <>
              <CheckCircleIcon sx={{ fontSize: '0.85rem', color: '#10B981' }} />
              <span
                style={{
                  color: '#10B981',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                }}
              >
                Compte rendu fait{' '}
                {compteRenduObj.statut ? `(${compteRenduObj.statut})` : ''}
              </span>
            </>
          ) : (
            <>
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'text.disabled',
                  ml: '3px',
                  mr: '2px',
                }}
              />
              <span style={{ color: '#64748B', fontSize: '0.6rem' }}>
                Pas de compte rendu
              </span>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ReservationCard;